import { useCallback, useEffect, useState } from 'react';

import type { WidgetTypingSignal } from '../types';

type TypingWindow = {
    startsAt: number;
    until: number;
};

/**
 * Mirrors the backend-owned typing window. The server decides when the
 * indicator starts and ends; this only schedules a local repaint at those
 * timestamps so the dots do not wait for the next poll.
 */
export function useTypingWindow() {
    const [typingWindow, setTypingWindow] = useState<TypingWindow | null>(null);
    const [visible, setVisible] = useState(false);

    const sync = useCallback((signal: WidgetTypingSignal) => {
        if (!signal.typing_starts_at || !signal.typing_until) {
            setTypingWindow(null);
            setVisible(false);

            return;
        }

        const startsAt = Date.parse(signal.typing_starts_at);
        const until = Date.parse(signal.typing_until);

        if (
            !Number.isFinite(startsAt) ||
            !Number.isFinite(until) ||
            until <= Date.now()
        ) {
            setTypingWindow(null);
            setVisible(false);

            return;
        }

        setTypingWindow((previous) =>
            previous?.startsAt === startsAt && previous.until === until
                ? previous
                : { startsAt, until },
        );
        setVisible(Date.now() >= startsAt);
    }, []);

    const hide = useCallback(() => {
        setTypingWindow(null);
        setVisible(false);
    }, []);

    useEffect(() => {
        if (!typingWindow) {
            return;
        }

        let timer: number | null = null;

        const tick = () => {
            const now = Date.now();

            if (now >= typingWindow.until) {
                setVisible(false);

                return;
            }

            if (now >= typingWindow.startsAt) {
                setVisible(true);
                timer = window.setTimeout(tick, typingWindow.until - now + 50);

                return;
            }

            setVisible(false);
            timer = window.setTimeout(tick, typingWindow.startsAt - now + 50);
        };

        timer = window.setTimeout(tick, 0);

        return () => {
            if (timer !== null) {
                window.clearTimeout(timer);
            }
        };
    }, [typingWindow]);

    return { visible, sync, hide } as const;
}
